import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { MatIcon } from "@angular/material/icon";
import { catchError, forkJoin, of } from "rxjs";
import { AsistenciaService } from "src/app/asistencia/asistencia.service";
import { soloFecha, soloHora } from "src/app/asistencia/formato-fecha";
import {
  DashboardGraphItem,
  DashboardKpi,
  GRAPH_COLORS,
  PERIODOS_DASHBOARD,
  PeriodoDashboard,
  RangoPeriodo,
  buildDonutStyle,
  formatMinutes,
  getMonthSortKey,
  obtenerRangoPeriodo,
  porcentaje,
  textoAFecha,
  toDateInput,
  toNumber,
} from "src/app/dashboard/shared/dashboard-graficas";
import { PeriodoDashboardComponent } from "src/app/dashboard/shared/periodo-dashboard/periodo-dashboard.component";
import { PestanaDashboard } from "src/app/dashboard/shared/pestana-dashboard";
import { Graficas } from "src/app/graficas/gestionGraficas/graficas.model";
import { AuthService } from "src/app/security/auth.service";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";
import {
  Dispositivo,
  Estacion,
  EstatusAsistencia,
  MonitoreoFiltro,
  MonitoreoRenglon,
} from "src/app/shared/model/asistencia/asistencia.model";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { AsistenciaDashboard, AsistenciaDashboardService } from "./asistencia-dashboard.service";

/** Un renglon de "Que atender": numero, lectura y, si hay pantalla, a donde ir. */
interface PendienteAsistencia {
  valor: number;
  texto: string;
  ruta: string | null;
}

/** Conteo por estatus tal como lo devuelve monitoreoResumen.do. */
type ResumenEstatus = { [estatus: string]: number };

/** Lo que devuelve monitoreo.do; null cuando la llamada fallo. */
type RespuestaMonitoreo = ConsultaList<MonitoreoRenglon> | null;

/** Codigo de retorno con el que el backend dice "salio bien". */
const COD_EXITO = "00000";

/** Tope del backend (MonitoreoBuss / AsistenciaDashboardBuss): mas de esto lo rechaza. */
const DIAS_MAXIMO_RANGO = 366;

/**
 * Pestaña "Asistencia" del Dashboard de Inicio: puntualidad, faltas y
 * retardos del periodo (monitoreoResumen.do), lo de hoy y las graficas
 * del endpoint propio (dashboard.do), los pendientes administrativos que
 * solo ve quien tiene su hoja de menu, y las ultimas faltas y retardos.
 *
 * Mientras el modulo no arranque (scripts sin correr, sin modalidades,
 * sin cierre) todo sale en cero: los estados vacios lo dicen tal cual.
 *
 * El periodo NO ofrece "Historico": el backend rechaza rangos de mas de
 * un ano, y el dashboard no es la puerta para bajar la historia entera.
 */
@Component({
  selector: "app-dashboard-asistencia",
  standalone: true,
  templateUrl: "./dashboard-asistencia.component.html",
  styleUrls: ["../../shared/dashboard-tarjetas.css", "./dashboard-asistencia.component.css"],
  imports: [CommonModule, MatIcon, PeriodoDashboardComponent],
})
export class DashboardAsistenciaComponent implements OnInit, PestanaDashboard {
  /** Sin "Historico": el backend acota a un ano. */
  readonly periodosAsistencia: PeriodoDashboard[] = PERIODOS_DASHBOARD.filter((periodo) => periodo.id !== "todo");
  readonly periodoInicial = "30";

  rango: RangoPeriodo = obtenerRangoPeriodo(this.periodoInicial);
  errorCarga = "";

  kpis: DashboardKpi[] = [];
  /** Dona: dias evaluados por estatus, en el orden de lectura del monitoreo. */
  estatusItems: DashboardGraphItem[] = [];
  totalDias = 0;
  /** Barras: como se registro la entrada. */
  origenItems: DashboardGraphItem[] = [];
  totalOrigen = 0;
  /** Tendencia: faltas y retardos por mes (ultimos doce). */
  mesItems: DashboardGraphItem[] = [];
  mesMax = 0;
  /** Ranking: sitios con mas faltas y retardos. */
  sitioItems: DashboardGraphItem[] = [];
  totalSitios = 0;
  /** "Que atender": lectura rapida del periodo y las bandejas administrativas gateadas. */
  pendientes: PendienteAsistencia[] = [];
  recientes: MonitoreoRenglon[] = [];

  puedeVerModalidades = false;
  puedeVerTelefonos = false;
  puedeVerEstaciones = false;

  private cargandoDatos = false;
  private readonly destroyRef = inject(DestroyRef);
  /** true desde que la pestaña se destruye; guarda el repintado (ver repintar()). */
  private destruido = false;

  /** Orden de lectura: primero lo que exige atencion (el mismo del monitoreo). */
  private readonly ordenEstatus: EstatusAsistencia[] = [
    "FALTA",
    "RETARDO",
    "PENDIENTE",
    "PUNTUAL",
    "JUSTIFICADO",
    "VACACIONES",
    "PERMISO",
    "DESCANSO",
    "SIN_HORARIO",
  ];

  /** Colores fijos por estatus, para que la dona se lea igual en cualquier periodo. */
  private readonly estatusColores: Record<string, string> = {
    FALTA: "#d32f2f",
    RETARDO: "#ef6c00",
    PENDIENTE: "#8d8d8d",
    PUNTUAL: "#00897b",
    JUSTIFICADO: "#1565c0",
    VACACIONES: "#6a5acd",
    PERMISO: "#0097a7",
    DESCANSO: "#455a64",
    SIN_HORARIO: "#b0bec5",
  };

  /** Como leer el origen de la entrada (OrigenChecadaEnum + SIN_REGISTRO del backend). */
  private readonly origenEtiquetas: Record<string, string> = {
    HUELLA: "Huella (kiosco)",
    APP: "Telefono (app)",
    QR: "Codigo QR",
    WEB: "Web",
    MANUAL: "Captura manual",
    SIN_REGISTRO: "Sin registro",
  };

  private readonly origenColores: Record<string, string> = {
    HUELLA: "#1565c0",
    APP: "#00897b",
    QR: "#6a5acd",
    WEB: "#455a64",
    MANUAL: "#ef6c00",
    SIN_REGISTRO: "#d32f2f",
  };

  constructor(
    private asistenciaService: AsistenciaService,
    private dashboardService: AsistenciaDashboardService,
    private authService: AuthService,
    private router: Router,
    // La vista NO se repinta sola cuando llega la respuesta HTTP: en este
    // arranque zone.js no queda parcheando XMLHttpRequest, asi que el
    // estado cambia y el DOM se queda en "Actualizando indicadores..."
    // hasta el siguiente clic. Se pide el repintado a mano, el mismo
    // patron que usa el resto de la casa (asistencia/mi-asistencia).
    // NO quitar por "innecesario".
    private cdr: ChangeDetectorRef,
  ) {
    // Se registra ANTES de cualquier suscripcion: los callbacks de
    // DestroyRef corren en orden de registro, asi que cuando
    // takeUntilDestroyed cierre el stream al destruir la pestaña (y con
    // ello dispare su complete) esto ya vale true.
    this.destroyRef.onDestroy(() => (this.destruido = true));
  }

  ngOnInit() {
    const menus = this.authService.getMenuList();
    this.puedeVerModalidades = tieneAccesoMenu(menus, "asistencia/modalidades");
    this.puedeVerTelefonos = tieneAccesoMenu(menus, "asistencia/telefonos");
    this.puedeVerEstaciones = tieneAccesoMenu(menus, "asistencia/estaciones");
    this.rango = obtenerRangoPeriodo(this.periodoInicial);
    this.consultar();
  }

  get cargando(): boolean {
    return this.cargandoDatos;
  }

  actualizar(): void {
    this.consultar();
  }

  onPeriodo(rango: RangoPeriodo): void {
    this.rango = rango;
    this.consultar();
  }

  consultar(): void {
    this.errorCarga = "";

    if (!this.rango.inicio || !this.rango.fin) {
      return;
    }
    if (this.diasDelRango() > DIAS_MAXIMO_RANGO) {
      this.errorCarga = "El rango no puede ser mayor a un ano; acote las fechas.";
      return;
    }

    this.cargandoDatos = true;
    const filtro = this.filtroPeriodo();

    forkJoin({
      resumen: this.asistenciaService.resumenMonitoreo(filtro).pipe(catchError(() => of(null as ResumenEstatus | null))),
      tablero: this.dashboardService.resumen(filtro).pipe(catchError(() => of(null as AsistenciaDashboard | null))),
      faltas: this.consultarRecientes("FALTA"),
      retardos: this.consultarRecientes("RETARDO"),
      telefonos: this.puedeVerTelefonos
        ? this.asistenciaService.consultarBandejaDispositivos("PENDIENTE")
            .pipe(catchError(() => of(null as ConsultaList<Dispositivo> | null)))
        : of(null as ConsultaList<Dispositivo> | null),
      estaciones: this.puedeVerEstaciones
        ? this.asistenciaService.consultarEstaciones()
            .pipe(catchError(() => of(null as ConsultaList<Estacion> | null)))
        : of(null as ConsultaList<Estacion> | null),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const resumen = res.resumen || {};
          const tablero = this.conExito(res.tablero) ? res.tablero : null;
          const faltas = this.conExito(res.faltas) ? res.faltas : null;
          const retardos = this.conExito(res.retardos) ? res.retardos : null;

          this.estatusItems = this.construirEstatus(resumen);
          this.totalDias = this.estatusItems.reduce((total, item) => total + item.valor, 0);
          this.origenItems = this.construirOrigen(tablero?.porOrigen || []);
          this.totalOrigen = this.origenItems.reduce((total, item) => total + item.valor, 0);
          this.mesItems = this.construirMeses(tablero?.porMes || []);
          this.mesMax = Math.max(...this.mesItems.map((item) => item.valor), 0);
          this.sitioItems = this.construirSitios(tablero?.porSitio || []);
          this.totalSitios = this.sitioItems.reduce((total, item) => total + item.valor, 0);
          this.recientes = this.construirRecientes([...(faltas?.list || []), ...(retardos?.list || [])]);
          this.kpis = this.construirKpis(resumen, tablero);
          this.pendientes = this.construirPendientes(resumen, tablero, res.telefonos, res.estaciones);

          const fallaron = [res.resumen === null, tablero === null, faltas === null, retardos === null];
          if (fallaron.every(Boolean)) {
            this.errorCarga = "No fue posible cargar la informacion de asistencia.";
          }
          this.repintar();
        },
        error: (e) => {
          console.error("Error al cargar dashboard de asistencia:", e);
          // RxJS no llama a complete despues de un error: hay que apagar la
          // carga aqui o la tarjeta "Actualizando indicadores..." se queda.
          this.cargandoDatos = false;
          this.errorCarga = "No fue posible cargar la informacion de asistencia.";
          this.repintar();
        },
        complete: () => {
          this.cargandoDatos = false;
          this.repintar();
        },
      });
  }

  /**
   * Repinta la pestaña. detectChanges() sobre una vista ya destruida
   * truena, y el complete SI llega al destruir (takeUntilDestroyed cierra
   * el stream desde el propio onDestroy), de ahi el guardia.
   */
  private repintar(): void {
    if (this.destruido) {
      return;
    }
    // markForCheck ADEMAS de detectChanges: detectChanges refresca solo
    // ESTA vista, y el cascaron (dashini) lee el `cargando` de la pestaña
    // para habilitar su boton "Actualizar". Sin marcar el camino hacia la
    // raiz ese enlace se queda con el valor viejo: el boton se queda
    // deshabilitado para siempre y en desarrollo sale un NG0100.
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  // ------------------------------------------------------------------
  // Lo que lee la plantilla
  // ------------------------------------------------------------------

  getDonutStyle(): Record<string, string> {
    if (this.totalDias <= 0 || this.estatusItems.length === 0) {
      return { background: "#eef1f5" };
    }
    return buildDonutStyle(this.estatusItems, this.totalDias);
  }

  getBarPercent(item: DashboardGraphItem, total: number): number {
    return porcentaje(item.valor, total);
  }

  getBarStyle(item: DashboardGraphItem, index: number, total: number): Record<string, string> {
    return {
      width: `${this.getBarPercent(item, total)}%`,
      background: item.color || GRAPH_COLORS[index % GRAPH_COLORS.length],
    };
  }

  getEstatusClass(renglon: MonitoreoRenglon): string {
    return `ticket-status--${String(renglon?.estatus || "sin").toLowerCase()}`;
  }

  /** "HH:mm" de la hora de entrada real; vacio si no hubo checada. */
  horaDe(valor: string | Date | null | undefined): string {
    return soloHora(valor);
  }

  abrirMonitoreo(): void {
    this.router.navigate(["/asistencia/monitoreo"]);
  }

  abrirPendiente(pendiente: PendienteAsistencia): void {
    if (pendiente?.ruta) {
      this.router.navigate([pendiente.ruta]);
    }
  }

  // ------------------------------------------------------------------
  // Consultas
  // ------------------------------------------------------------------

  private filtroPeriodo(): MonitoreoFiltro {
    const filtro = new MonitoreoFiltro();
    filtro.desde = this.rango.inicio;
    filtro.hasta = this.rango.fin;
    return filtro;
  }

  /** Las seis ultimas de un estatus; monitoreo.do ya ordena por fecha descendente. */
  private consultarRecientes(estatus: EstatusAsistencia) {
    const filtro = this.filtroPeriodo();
    filtro.estatus = estatus;
    filtro.rowInicio = 0;
    filtro.regPagina = 6;
    return this.asistenciaService.consultarMonitoreo(filtro).pipe(
      catchError(() => of(null as RespuestaMonitoreo))
    );
  }

  private diasDelRango(): number {
    const inicio = textoAFecha(this.rango.inicio);
    const fin = textoAFecha(this.rango.fin);
    if (!inicio || !fin) {
      return 0;
    }
    return Math.round((fin.getTime() - inicio.getTime()) / 86400000);
  }

  /** Un ConsultaList o bean sin codigo, o con el de exito, salio bien; el resto fallo en el servidor. */
  private conExito<T extends { pCodRetorno?: string }>(respuesta: T | null): respuesta is T {
    if (!respuesta) {
      return false;
    }
    return !respuesta.pCodRetorno || respuesta.pCodRetorno === COD_EXITO;
  }

  // ------------------------------------------------------------------
  // Armado
  // ------------------------------------------------------------------

  private conteo(resumen: ResumenEstatus, estatus: EstatusAsistencia): number {
    return toNumber(resumen?.[estatus]);
  }

  private construirKpis(resumen: ResumenEstatus, tablero: AsistenciaDashboard | null): DashboardKpi[] {
    const puntual = this.conteo(resumen, "PUNTUAL");
    const justificado = this.conteo(resumen, "JUSTIFICADO");
    const retardo = this.conteo(resumen, "RETARDO");
    const falta = this.conteo(resumen, "FALTA");
    const pendiente = this.conteo(resumen, "PENDIENTE");
    const evaluados = puntual + justificado + retardo + falta;
    const minutos = toNumber(tablero?.minutosRetardoPromedio);
    const activos = toNumber(tablero?.activos);

    return [
      {
        label: "Puntualidad",
        value: evaluados > 0 ? `${porcentaje(puntual + justificado, evaluados)}%` : "--",
        meta: evaluados > 0 ? `${evaluados} dias evaluados` : "Sin dias evaluados",
        icon: "how_to_reg",
        className: "ticket-kpi--done",
      },
      {
        label: "Faltas",
        value: falta,
        meta: `${pendiente} dias sin cerrar`,
        icon: "person_off",
        className: "ticket-kpi--total",
      },
      {
        label: "Retardos",
        value: retardo,
        meta: minutos > 0 ? `promedio ${formatMinutes(minutos)}` : "Sin minutos acumulados",
        icon: "timer_off",
        className: "ticket-kpi--time",
      },
      {
        label: "Presentes hoy",
        value: toNumber(tablero?.presentesHoy),
        meta: `de ${activos} activos`,
        icon: "fingerprint",
        className: "ticket-kpi--active",
      },
      {
        label: "Sin registrar hoy",
        value: toNumber(tablero?.sinRegistrarHoy),
        meta: "Sin veredicto todavia",
        icon: "pending_actions",
        className: "ticket-kpi--muted",
      },
    ];
  }

  /** Dona en el orden de lectura del monitoreo, solo con los estatus que tienen dias. */
  private construirEstatus(resumen: ResumenEstatus): DashboardGraphItem[] {
    return this.ordenEstatus
      .map((estatus) => ({
        nombre: estatus.replace("_", " "),
        valor: this.conteo(resumen, estatus),
        color: this.estatusColores[estatus],
      }))
      .filter((item) => item.valor > 0);
  }

  private construirOrigen(items: Graficas[]): DashboardGraphItem[] {
    return items
      .filter((item) => item && item.valor1 !== null && item.valor1 !== undefined)
      .map((item, index) => {
        const clave = String(item.valor1).toUpperCase();
        return {
          nombre: this.origenEtiquetas[clave] || clave,
          valor: toNumber(item.valor2),
          color: this.origenColores[clave] || GRAPH_COLORS[index % GRAPH_COLORS.length],
        };
      })
      .filter((item) => item.valor > 0)
      .sort((a, b) => b.valor - a.valor);
  }

  /** El backend ya viene en orden cronologico; se reordena por si acaso y se pinta con un solo color. */
  private construirMeses(items: Graficas[]): DashboardGraphItem[] {
    return items
      .filter((item) => item && item.valor1)
      .map((item) => ({
        nombre: String(item.valor1),
        valor: toNumber(item.valor2),
        color: this.estatusColores["RETARDO"],
      }))
      .sort((a, b) => getMonthSortKey(a.nombre) - getMonthSortKey(b.nombre))
      .slice(-12);
  }

  /** Se respeta el nombre del sitio tal cual (no en mayusculas) y el orden del backend. */
  private construirSitios(items: Graficas[]): DashboardGraphItem[] {
    return items
      .filter((item) => item && item.valor1)
      .map((item, index) => ({
        nombre: String(item.valor1),
        valor: toNumber(item.valor2),
        color: GRAPH_COLORS[index % GRAPH_COLORS.length],
      }))
      .filter((item) => item.valor > 0)
      .slice(0, 6);
  }

  /** Faltas y retardos mezclados, del mas reciente al mas viejo, y se cortan a seis. */
  private construirRecientes(renglones: MonitoreoRenglon[]): MonitoreoRenglon[] {
    return renglones
      .filter((renglon) => renglon && renglon.fecha)
      .sort((a, b) => String(b.fecha).localeCompare(String(a.fecha)))
      .slice(0, 6);
  }

  private construirPendientes(
    resumen: ResumenEstatus,
    tablero: AsistenciaDashboard | null,
    telefonos: ConsultaList<Dispositivo> | null,
    estaciones: ConsultaList<Estacion> | null
  ): PendienteAsistencia[] {
    const pendientes: PendienteAsistencia[] = [
      {
        valor: toNumber(tablero?.ubicacionDistinta),
        texto: "dias con checada en un sitio distinto al esperado",
        ruta: "/asistencia/monitoreo",
      },
      {
        valor: this.conteo(resumen, "PENDIENTE"),
        texto: "dias del periodo sin cerrar todavia",
        ruta: "/asistencia/monitoreo",
      },
    ];

    // El numero sale del endpoint propio y NO de plantilla.do: ese conteo
    // no filtra el estatus del usuario y podria pasarse de "de N activos".
    if (this.puedeVerModalidades && tablero) {
      pendientes.push({
        valor: toNumber(tablero.activosSinModalidad),
        texto: "personas activas sin modalidad vigente: no se evaluan",
        ruta: "/asistencia/modalidades",
      });
    }

    if (this.puedeVerTelefonos && this.conExito(telefonos)) {
      pendientes.push({
        valor: (telefonos.list || []).length,
        texto: "telefonos por aprobar: no pueden registrar",
        ruta: "/asistencia/telefonos",
      });
    }

    if (this.puedeVerEstaciones && this.conExito(estaciones)) {
      const activas = (estaciones.list || []).filter((estacion) => this.esActiva(estacion));
      if (activas.length > 0) {
        // Sin sincronizar = nunca (vacio) o antes de hoy: "yyyy-MM-dd" se compara como texto.
        const hoy = toDateInput(new Date());
        const sinSincronizar = activas.filter((estacion) => soloFecha(estacion.ultimaSincronizacion) < hoy);
        pendientes.push({
          valor: sinSincronizar.length,
          texto: `kioscos sin sincronizar hoy (de ${activas.length} activos)`,
          ruta: "/asistencia/estaciones",
        });
      }
    }

    return pendientes;
  }

  private esActiva(estacion: Estacion): boolean {
    return String(estacion?.estatus || "").toUpperCase() === "ACTIVO";
  }
}
